import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Video, Pencil, Trash2, FolderPlus, ChevronDown, Trash } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useVideoGroups } from '../../hooks/useVideoGroups';
import { getBaseVideoList } from '../../services/video';
import apiService from '../../services/api';
import './MyVideos.css';

const PAGE_SIZE = 12;

const getStatusPill = (status) => {
  switch (status) {
    case 4: return { label: '已完成', cls: 'mvid-status-pill--success' };
    case 3: return { label: '失败',   cls: 'mvid-status-pill--failed' };
    case 2: return { label: '制作中', cls: 'mvid-status-pill--processing' };
    case 1: return { label: '排队中', cls: 'mvid-status-pill--queued' };
    default: return { label: '未知',  cls: 'mvid-status-pill--queued' };
  }
};

const extractMeta = (response, fallback = 0) => {
  if (!response) return { totalItems: fallback, pageSize: PAGE_SIZE };
  const d = response.data ?? {};
  return {
    totalItems: Number(d.total ?? d.totalCount ?? response.total ?? response.totalCount ?? 0) || fallback,
    pageSize:   Number(d.pageSize ?? response.pageSize ?? PAGE_SIZE) || PAGE_SIZE,
  };
};

// "全部" tab — shows all videos, no client-side filtering
const ALL_TAB = 'all';

const MyVideos = () => {
  const { user } = useAuth();
  const { groups, assignments, createGroup, renameGroup, deleteGroup, assignVideos } =
    useVideoGroups(user?.phone);

  // ── Server-side pagination state ──
  const [videoListResponse, setVideoListResponse] = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [apiError, setApiError]                   = useState('');
  const [currentPage, setCurrentPage]             = useState(1);
  const [retryCount, setRetryCount]               = useState(0);

  // ── UI state ──
  const [searchQuery, setSearchQuery]       = useState('');
  const [activeGroupTab, setActiveGroupTab] = useState(ALL_TAB);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [showMoveMenu, setShowMoveMenu]     = useState(false);

  // Video rename
  const [renamingVideoId, setRenamingVideoId]   = useState(null);
  const [renameVideoValue, setRenameVideoValue] = useState('');
  const renameVideoInputRef = useRef(null);

  // Group tab rename
  const [renamingId, setRenamingId]   = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);

  // New group input
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName]           = useState('');
  const newGroupInputRef = useRef(null);

  // ── Fetch one page at a time (server-side pagination) ──
  useEffect(() => {
    if (!user?.phone) return;
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setApiError('');
      try {
        const data = await getBaseVideoList({
          phone: user.phone,
          page: currentPage,
          pageSize: PAGE_SIZE,
          search: searchQuery,
          groupId: activeGroupTab === ALL_TAB ? '' : activeGroupTab,
        });
        if (cancelled) return;
        if (data.code !== undefined && data.code !== 0) throw new Error(data.message || `Error ${data.code}`);
        setVideoListResponse(data);
      } catch (err) {
        if (!cancelled) { setApiError(err.message || '加载失败'); setVideoListResponse(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [user?.phone, currentPage, searchQuery, retryCount, activeGroupTab]);

  const refetchVideos = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  // ── Map server response → normalized video array ──
  const serverVideos = useMemo(() => {
    if (!videoListResponse) return [];
    return (Array.isArray(videoListResponse.videos) ? videoListResponse.videos : [])
      .map((item, idx) => {
        // Generate a unique id if item.id is missing or invalid
        const itemId = item.id || `vid-${Date.now()}-${idx}`;
        // Use taskId as fallback for id, and vice versa
        const primaryId = item.task_id && item.task_id !== '' ? item.task_id : itemId;
        const numericId = item.id || (item.task_id && !isNaN(parseInt(item.task_id)) ? parseInt(item.task_id) : null);

        const video = {
          id: itemId,
          title:     item.title     || item.name     || `视频 ${idx + 1}`,
          text:      item.text      || '',
          createdAt: item.created_at || item.createdAt || '',
          thumbnail: item.thumbnail  || item.cover_url || '',
          videoUrl:  item.video_url  || item.url       || '',
          previewUrl: item.preview_url || item.previewUrl || '',  // 预览图
          status:    item.status    || 0,
          taskId:    item.task_id   || numericId || itemId,
        };

        // Debug: log first video to check data structure
        if (idx === 0) {
          console.log('🔍 First video data:', item);
          console.log('🔍 Mapped video:', video);
          console.log('🔍 Has previewUrl:', !!video.previewUrl);
        }

        return video;
      });
  }, [videoListResponse]);

  const responseMeta = useMemo(() => extractMeta(videoListResponse, serverVideos.length), [videoListResponse, serverVideos.length]);

  // ── Tab filtering ──
  // "全部" tab: show ALL videos from current page (server-side pagination is correct).
  // Group tabs: Now the backend returns videos filtered by groupId, so we don't need client-side filtering.
  // However, we still use assignments for showing group badges on the "全部" tab.
  const tabVideos = useMemo(() => {
    // Backend now handles group filtering, so just return serverVideos
    return serverVideos;
  }, [serverVideos]);

  // ── Search filter ──
  const filteredVideos = useMemo(() => {
    return tabVideos.filter((v) =>
      !searchQuery || v.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [tabVideos, searchQuery]);

  // ── Pagination ──
  // "全部" tab: use server total (correct across all pages).
  // Group tabs: use client-filtered count (current-page only — known limitation).
  const totalCount = filteredVideos.length;
  const totalPages = activeGroupTab === ALL_TAB
    ? (Math.ceil(responseMeta.totalItems / PAGE_SIZE) || 1)
    : (Math.ceil(totalCount / PAGE_SIZE) || 1);

  // ── Reset page when tab or search changes ──
  useEffect(() => { setCurrentPage(1); setSelectedVideos([]); }, [activeGroupTab, searchQuery]);

  // ── Clamp page ──
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    else if (totalPages === 0 && currentPage !== 1) setCurrentPage(1);
  }, [totalPages, currentPage]);

  // ── Focus helpers ──
  useEffect(() => { if (showNewGroupInput) newGroupInputRef.current?.focus(); }, [showNewGroupInput]);
  useEffect(() => { if (renamingId) renameInputRef.current?.focus(); }, [renamingId]);
  useEffect(() => { if (renamingVideoId) renameVideoInputRef.current?.focus(); }, [renamingVideoId]);

  // ── Group badge helper ──
  const getGroupName = useCallback((videoId) => {
    const groupId = assignments[String(videoId)];
    if (!groupId) return null;
    return groups.find((g) => g.id === groupId)?.name ?? null;
  }, [assignments, groups]);

  // ── Group operations ──
  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) return;
    if (groups.some((g) => g.name === name)) {
      alert(`分组名称「${name}」已存在，请使用其他名称`);
      return;
    }
    try {
      const id = await createGroup(name);
      setActiveGroupTab(id);
      setNewGroupName('');
      setShowNewGroupInput(false);
    } catch (error) {
      alert(`创建分组失败: ${error.message || '请重试'}`);
    }
  }, [newGroupName, createGroup, groups]);

  const handleStartRename = useCallback(() => {
    const g = groups.find((g) => g.id === activeGroupTab);
    if (!g) return;
    setRenamingId(g.id);
    setRenameValue(g.name);
  }, [groups, activeGroupTab]);

  const handleConfirmRename = useCallback(() => {
    const name = renameValue.trim();
    if (!name || !renamingId) { setRenamingId(null); return; }
    if (groups.some((g) => g.id !== renamingId && g.name === name)) {
      alert(`分组名称「${name}」已存在，请使用其他名称`);
      return;
    }
    renameGroup(renamingId, name);
    setRenamingId(null);
  }, [renameValue, renamingId, renameGroup, groups]);

  const handleDeleteActiveGroup = useCallback(() => {
    const g = groups.find((g) => g.id === activeGroupTab);
    if (!g) return;
    if (!window.confirm(`确认删除分组「${g.name}」？\n该分组内的视频将归回"全部"。`)) return;
    deleteGroup(g.id);
    setActiveGroupTab(ALL_TAB);
  }, [groups, activeGroupTab, deleteGroup]);

  // ── Selection & batch move ──
  const toggleSelect = useCallback((id) => {
    setSelectedVideos((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }, []);

  const handleMoveToGroup = useCallback(async (groupId) => {
    try {
      await assignVideos(selectedVideos, groupId || null);
      setSelectedVideos([]);
      setShowMoveMenu(false);
    } catch (error) {
      alert(`分配失败: ${error.message || '请重试'}`);
    }
  }, [selectedVideos, assignVideos]);

  const handleBatchDelete = useCallback(async () => {
    if (selectedVideos.length === 0) return;
    if (!window.confirm(`确认删除选中的 ${selectedVideos.length} 个视频？删除后无法恢复。`)) return;
    try {
      await apiService.batchDeleteVideos(selectedVideos);
      // 刷新列表以反映删除结果
      await refetchVideos();
      setSelectedVideos([]);
      alert(`成功删除 ${selectedVideos.length} 个视频`);
    } catch (err) {
      alert(err.message || '批量删除失败，请重试');
    }
  }, [selectedVideos, refetchVideos]);

  // ── Video rename ──
  const handleStartRenameVideo = (video, e) => {
    e.stopPropagation();
    setRenamingVideoId(video.id);
    setRenameVideoValue(video.title);
  };

  const handleConfirmRenameVideo = useCallback(async () => {
    const name = renameVideoValue.trim();
    if (!name || !renamingVideoId) { setRenamingVideoId(null); return; }
    try {
      await apiService.renameVideo(renamingVideoId, name);
      setVideoListResponse((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          videos: (prev.videos || []).map((v) =>
            v.id === renamingVideoId ? { ...v, title: name } : v
          ),
        };
      });
      setRenamingVideoId(null);
    } catch (err) {
      alert(err.message || '重命名失败，请重试');
    }
  }, [renameVideoValue, renamingVideoId]);

  // ── Video actions ──
  const handleDownload = (video, e) => {
    e.stopPropagation();
    if (video.videoUrl) window.open(video.videoUrl, '_blank');
  };

  const handleDelete = async (video, e) => {
    e.stopPropagation();
    if (!window.confirm(`确认删除视频「${video.title}」？删除后无法恢复。`)) return;
    try {
      await apiService.deleteVideo(video.id);
      setVideoListResponse((prev) => {
        if (!prev) return prev;
        return { ...prev, videos: (prev.videos || []).filter((v) => v.id !== video.id) };
      });
    } catch (err) {
      alert(err.message || '删除失败，请重试');
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;
    setCurrentPage(page);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '我的视频', path: null },
  ];

  const moveTargets = groups.filter((g) => g.id !== activeGroupTab);

  return (
    <div className="mvid-page">
      <Breadcrumb items={breadcrumbItems} />

      <div className="mvid-page-header">
        <h1 className="mvid-page-title">我的视频</h1>
      </div>

      {/* ── Tab Bar ── */}
      <div className="mvid-tab-bar">
        {/* "全部" tab — server-paginated, no client-side filtering */}
        <button
          className={`mvid-tab${activeGroupTab === ALL_TAB ? ' active' : ''}`}
          onClick={() => setActiveGroupTab(ALL_TAB)}
        >
          全部
        </button>

        {groups.map((g) => (
          <button
            key={g.id}
            className={`mvid-tab${activeGroupTab === g.id ? ' active' : ''}`}
            onClick={() => setActiveGroupTab(g.id)}
          >
            <span className="mvid-tab-name">{g.name}</span>
          </button>
        ))}

        {showNewGroupInput ? (
          <div className="mvid-new-group-input-wrap">
            <input
              ref={newGroupInputRef}
              className="mvid-new-group-input"
              placeholder="分组名称"
              value={newGroupName}
              maxLength={20}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateGroup();
                if (e.key === 'Escape') { setShowNewGroupInput(false); setNewGroupName(''); }
              }}
            />
            <button className="mvid-new-group-confirm" onClick={handleCreateGroup}>确认</button>
            <button
              className="mvid-new-group-cancel"
              onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); }}
            >取消</button>
          </div>
        ) : (
          <button className="mvid-new-group-btn" onClick={() => setShowNewGroupInput(true)}>
            <FolderPlus size={14} />新建分组
          </button>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className="mvid-toolbar">
        <input
          className="mvid-search-box"
          placeholder="搜索视频..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="mvid-total-count">
          共 {activeGroupTab === ALL_TAB ? (responseMeta.totalItems || totalCount) : totalCount} 个视频
          {activeGroupTab !== ALL_TAB && (
            <span className="mvid-group-scope-hint">（本页）</span>
          )}
        </span>

        {activeGroupTab !== ALL_TAB && (
          renamingId ? (
            <div className="mvid-group-rename-wrap">
              <input
                ref={renameInputRef}
                className="mvid-group-rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmRename();
                  if (e.key === 'Escape') setRenamingId(null);
                }}
              />
              <button className="mvid-group-rename-ok" onClick={handleConfirmRename}>确认</button>
              <button className="mvid-group-rename-cancel" onClick={() => setRenamingId(null)}>取消</button>
            </div>
          ) : (
            <div className="mvid-group-actions">
              <button className="mvid-group-rename-btn" onClick={handleStartRename}>
                <Pencil size={13} />重命名
              </button>
              <button className="mvid-group-del-btn" onClick={handleDeleteActiveGroup}>
                <Trash2 size={13} />删除分组
              </button>
            </div>
          )
        )}
      </div>

      {/* ── Batch Action Bar ── */}
      {selectedVideos.length > 0 && (
        <div className="mvid-batch-bar">
          <span className="mvid-batch-info">已选 {selectedVideos.length} 个视频</span>

          <div className="mvid-move-wrap">
            {showMoveMenu && (
              <div className="mvid-move-backdrop" onClick={() => setShowMoveMenu(false)} />
            )}
            <button className="mvid-move-btn" onClick={() => setShowMoveMenu((p) => !p)}>
              移至分组 <ChevronDown size={13} />
            </button>
            {showMoveMenu && (
              <div className="mvid-move-menu">
                {moveTargets.length === 0 ? (
                  <div className="mvid-move-item" style={{ color: '#9ca3af', cursor: 'default' }}>
                    暂无分组，请先新建
                  </div>
                ) : (
                  moveTargets.map((g) => (
                    <button key={g.id} className="mvid-move-item" onClick={() => handleMoveToGroup(g.id)}>
                      {g.name}
                    </button>
                  ))
                )}
                {activeGroupTab !== ALL_TAB && (
                  <button className="mvid-move-item ungrouped" onClick={() => handleMoveToGroup(null)}>
                    移出分组
                  </button>
                )}
              </div>
            )}
          </div>

          <button className="mvid-batch-del-btn" onClick={handleBatchDelete}>
            <Trash size={13} />批量删除
          </button>
          <button className="mvid-cancel-btn" onClick={() => setSelectedVideos([])}>取消选择</button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="mvid-loading"><LoadingSpinner /></div>
      ) : apiError ? (
        <div className="mvid-error">
          加载失败：{apiError}
          <button onClick={() => setVideoListResponse(null)}>重试</button>
        </div>
      ) : filteredVideos.length === 0 ? (
        <div className="mvid-empty">
          <Video size={48} className="mvid-empty-icon" />
          <p className="mvid-empty-text">
            {searchQuery ? '没有找到匹配的视频' : activeGroupTab !== ALL_TAB ? '本页没有属于此分组的视频' : '还没有视频，去生成你的第一个视频吧'}
          </p>
        </div>
      ) : (
        <>
          <div className="mvid-cards-grid">
            {filteredVideos.map((video) => {
              const pill        = getStatusPill(video.status);
              const isSelected  = selectedVideos.includes(video.id);
              const isCompleted = video.status === 4;
              const groupName   = getGroupName(video.id);

              return (
                <div
                  key={video.id}
                  className={`mvid-video-card${isSelected ? ' selected' : ''}`}
                >
                  <div
                    className="mvid-thumb"
                    onClick={() => toggleSelect(video.id)}
                  >
                    {video.videoUrl ? (
                      <video
                        className="mvid-thumb-media"
                        src={video.videoUrl}
                        preload="metadata"
                        poster={video.thumbnail || video.previewUrl || undefined}
                      />
                    ) : video.thumbnail || video.previewUrl ? (
                      <img
                        className="mvid-thumb-media"
                        src={video.thumbnail || video.previewUrl}
                        alt={video.title}
                      />
                    ) : (
                      <div className="mvid-thumb-placeholder">
                        <Video size={36} />
                      </div>
                    )}

                    <span className={`mvid-status-pill ${pill.cls}`}>{pill.label}</span>

                    {/* Group badge — visible in "全部" tab so user knows which group a video belongs to */}
                    {groupName && activeGroupTab === ALL_TAB && (
                      <span className="mvid-group-badge">{groupName}</span>
                    )}

                    <div className="mvid-check-overlay" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="mvid-check-input"
                        checked={isSelected}
                        onChange={() => toggleSelect(video.id)}
                      />
                    </div>
                  </div>

                  <div className="mvid-body">
                    {renamingVideoId === video.id ? (
                      <>
                        <input
                          ref={renameVideoInputRef}
                          className="mvid-title-input"
                          value={renameVideoValue}
                          maxLength={50}
                          onChange={(e) => setRenameVideoValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmRenameVideo();
                            if (e.key === 'Escape') setRenamingVideoId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="mvid-title-input-actions">
                          <button className="mvid-title-confirm" onClick={handleConfirmRenameVideo}>确认</button>
                          <button className="mvid-title-cancel" onClick={() => setRenamingVideoId(null)}>取消</button>
                        </div>
                      </>
                    ) : (
                      <p className="mvid-title" title={video.title}>{video.title}</p>
                    )}
                    {video.createdAt && <span className="mvid-meta">{video.createdAt}</span>}
                    {video.text && (
                      <span className="mvid-script" title={video.text}>{video.text}</span>
                    )}
                  </div>

                  <div className="mvid-actions">
                    {isCompleted && video.videoUrl && (
                      <button className="mvid-dl-btn" onClick={(e) => handleDownload(video, e)}>
                        下载
                      </button>
                    )}
                    <button
                      className="mvid-rename-btn"
                      onClick={(e) => handleStartRenameVideo(video, e)}
                      disabled={renamingVideoId === video.id}
                    >
                      重命名
                    </button>
                    <button className="mvid-del-btn" onClick={(e) => handleDelete(video, e)}>
                      删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mvid-pagination">
              <button
                className="mvid-page-btn"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >←</button>

              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let n;
                if (totalPages <= 7)                    n = i + 1;
                else if (currentPage <= 4)               n = i + 1;
                else if (currentPage >= totalPages - 3)  n = totalPages - 6 + i;
                else                                     n = currentPage - 3 + i;
                return (
                  <button
                    key={n}
                    className={`mvid-page-num${currentPage === n ? ' active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >{n}</button>
                );
              })}

              <button
                className="mvid-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyVideos;
